/**
 * 元動画を時計にする `ClockPort`（索引 Issue #498 の段階 8）。**`duelEditor/timeline/` の外**。
 *
 * **演出の画面（`/duel-timing`・`/duel-editor`）の時刻の入口はこれ 1 本**:
 * 再生中は `requestVideoFrameCallback`（動画のフレームが出るたび）、止まっている間は seeked / timeupdate。
 * 動画に時刻を持たせるのは、演出と実写がずれないため —— 別の時計で進めると必ず離れていく。
 *
 * 書き出しのときはこれを使わず `createManualClock`（外からフレームを進める）に差し替える。
 */
import { ClockPort } from '../duelEditor/timeline/ports/clock';

export function createVideoClock() {
  const listeners = new Set();
  let el = null;
  let ms = 0;
  let lastVolume = 1;
  let handle = null;
  let detach = null;

  const emit = () => listeners.forEach((fn) => fn(ms));
  const setFromElement = () => {
    if (!el) return;
    const next = Math.max(0, (el.currentTime ?? 0) * 1000);
    if (Math.abs(next - ms) < 0.5) return;
    ms = next;
    emit();
  };
  const frameLoop = (now, meta) => {
    if (!el) return;
    ms = Math.max(0, ((meta?.mediaTime ?? el.currentTime) ?? 0) * 1000);
    emit();
    if (!el.paused) schedule();
  };
  function schedule() {
    if (!el) return;
    if (typeof el.requestVideoFrameCallback === 'function') handle = el.requestVideoFrameCallback(frameLoop);
    else handle = window.requestAnimationFrame(() => frameLoop());
  }

  const port = ClockPort.create({
    ms: () => ms,
    seek: (v) => {
      const sec = Math.max(0, v / 1000);
      if (el) el.currentTime = sec;
      ms = sec * 1000;
      emit();
    },
    play: (rate = 1) => { if (el) { el.playbackRate = rate; el.play?.(); schedule(); } },
    pause: () => { el?.pause?.(); },
    paused: () => el?.paused ?? true,
    rate: () => el?.playbackRate ?? 1,
    duration: () => (Number.isFinite(el?.duration) ? el.duration * 1000 : 0),
    // 元動画の音。**既定で鳴らす**（書き出しのときだけ呼ぶ側が 0 にする）
    volume: () => (el ? (el.muted ? 0 : el.volume) : lastVolume),
    setVolume: (v) => {
      lastVolume = Math.max(0, Math.min(1, v));
      if (el) { el.volume = lastVolume; el.muted = lastVolume === 0; }
      emit();
    },
    subscribe: (fn) => { listeners.add(fn); return () => listeners.delete(fn); },
  });

  return {
    ...port,
    /**
     * `<video>` を繋ぐ（`SourceVideo` の `onElement` から呼ぶ）。
     *
     * **`null` は無視する**。`SourceVideo` の ref はインライン関数なので、React は**再描画のたびに**
     * `null` → 要素 の順で呼ぶ。素直に外して繋ぎ直すと、そのたびに今の位置を知らせることになり、
     * 再生中は「知らせる → 再描画 → 繋ぎ直し → 知らせる」の無限ループ（React error #185）になる。
     * 外すのは `dispose()` か、別の要素に差し替わったとき。
     */
    attach(node) {
      if (!node || node === el) return;
      detach?.();
      el = node;
      const on = (type, fn) => { node.addEventListener(type, fn); return () => node.removeEventListener(type, fn); };
      const offs = [
        on('seeked', setFromElement), on('timeupdate', setFromElement),
        on('loadedmetadata', setFromElement),
        // 再生 / 一時停止は位置が変わらなくても知らせる（editor の `transport.playing` を合わせる）
        on('play', () => { schedule(); emit(); }), on('pause', () => { setFromElement(); emit(); }),
      ];
      detach = () => offs.forEach((off) => off());
      node.volume = lastVolume;
      node.muted = lastVolume === 0;
      setFromElement();
      if (!node.paused) schedule();
    },
    dispose() {
      detach?.();
      detach = null;
      el = null;
      if (handle != null && typeof window !== 'undefined') window.cancelAnimationFrame?.(handle);
      listeners.clear();
    },
  };
}
